import "@/components/home/blog.css";
import "@/components/ui/atoms/categories/data-display.css";
import Image from "next/image";
import { memo } from "react";

export type Author = { name: string; src: string };

/**
 * AuthorRow — the blog byline: the overlapped avatar stack, then the author
 * names and the read time joined by a middot.
 *
 * Two sites wrote it. The blog index (blog-content.tsx) held it as a
 * module-local component that PostCard rendered per card; the article
 * (blog-post-clerk.tsx) re-typed the same markup by hand in its header, down
 * to the empty alt, the 24×24 Image and the aria-hidden separator span. Only
 * the strings differed, and those were already props on one side.
 *
 * One real difference survives: the article's header hangs the row off the
 * title rather than pushing it to the bottom of a card. That placement is
 * `variant="post"`, stamped as `data-variant` on the row's root.
 *
 * The directory rows and the related-posts list print `dw-text-body` in the muted tone,
 * a names-only line with no avatars and no read-time element — and the article
 * passes a pre-composed byline string into it. That is a different shape and
 * this component does not own it.
 */
export const AuthorRow = memo(function AuthorRow({
  authors,
  read,
  variant,
}: {
  authors: readonly Author[];
  read: string;
  variant?: "post";
}) {
  return (
    <div className="dw-blog__byline" data-variant={variant} data-component="AuthorRow" data-location="src/components/home/author-row.tsx">
      <div className="dw-blog__avatars" aria-hidden>
        {authors.map((author) => (
          <span key={author.name} className="dw-blog__avatar dw-icon-box" data-icon-size="xl">
            <Image src={author.src} alt="" aria-hidden="true" width={24} height={24} className="dw-blog__avatar-img" />
          </span>
        ))}
      </div>
      <p className="dw-text-body" data-tone="muted">
        {authors.map((author) => author.name).join(", ")}
        <span aria-hidden> · </span>
        {read}
      </p>
    </div>
  );
});
